import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { adminAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Users, Image, Megaphone, BarChart3, Ticket, TrendingUp, Shield, FileText, PhoneOff, MessageCircle,
  ChevronLeft, ChevronRight, Loader2, Trash2,
  Plus, Pencil, ArrowLeft, Search, X,
  DollarSign, Download, UserCheck, UserX, Lock, FileDown, Filter,
} from "lucide-react";
import { AdminCouponsTab } from "@/components/admin/AdminCouponsTab";
import { AdminAnalyticsTab } from "@/components/admin/AdminAnalyticsTab";
import { AdminSecurityTab } from "@/components/admin/AdminSecurityTab";
import { AdminProductReportTab } from "@/components/admin/AdminProductReportTab";
import { AdminOtpDropoffsTab } from "@/components/admin/AdminOtpDropoffsTab";
import { AdminWaCampaignsTab } from "@/components/admin/AdminWaCampaignsTab";

/* ─── PIN Verification Modal ──────────────────────────────── */
function PinModal({ label, onConfirm, onCancel }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!pin.trim()) { setError("PIN is required"); return; }
    setLoading(true);
    setError("");
    try {
      await onConfirm(pin);
    } catch (err) {
      const msg = err.response?.data?.detail || "Verification failed";
      setError(msg);
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center" data-testid="pin-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative z-10 bg-white rounded-xl shadow-xl max-w-xs w-full mx-4 p-6 text-center">
        <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
          <Lock className="h-5 w-5 text-amber-600" />
        </div>
        <h3 className="text-sm font-bold text-[#09090B] mb-1" style={{ fontFamily: "Manrope, sans-serif" }}>
          Admin PIN Required
        </h3>
        <p className="text-xs text-slate-500 mb-4">{label || "Enter your admin PIN to continue"}</p>
        <Input
          type="password"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          placeholder="Enter PIN"
          className="text-center mb-2"
          autoFocus
          data-testid="pin-input"
        />
        {error && <p className="text-xs text-red-500 mb-2" data-testid="pin-error">{error}</p>}
        <div className="flex gap-2 mt-3">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={loading} className="flex-1" data-testid="pin-cancel">Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={loading} className="flex-1 bg-[#18181B] hover:bg-[#27272A]" data-testid="pin-confirm">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Confirm"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── Metric Card ─────────────────────────────────────────── */
function MetricCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`metric-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">{label}</p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <p className="text-2xl font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
        {typeof value === "number" ? value.toLocaleString("en-IN") : value}
      </p>
    </div>
  );
}

/* ─── Simple Pagination ───────────────────────────────────── */
function SimplePagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 text-sm" data-testid="admin-pagination">
      <span className="text-slate-500">Page {page} of {totalPages}</span>
      <div className="flex gap-1">
        <Button variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={page <= 1} className="h-8">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} className="h-8">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ─── User Modal (create / edit) ──────────────────────────── */
function UserModal({ user, onClose, onSave }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    phone: user?.phone || "+91",
    email: user?.email || "",
    plan: user?.plan || "free",
    notes: user?.notes || "",
    access_granted_reason: user?.access_granted_reason || "",
    free_download_limit: user?.free_download_limit ?? 2,
    downloads_used: user?.downloads_used ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const phoneChanged = isEdit && form.phone !== user.phone;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="user-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-bold text-[#09090B] mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>
          {isEdit ? "Edit User" : "Create User"}
        </h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Phone</label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-phone-input" />
            {phoneChanged && (
              <p className="text-xs text-amber-600 mt-1">This will change the user's login identity.</p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Email (optional)</label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Plan</label>
            <div className="flex gap-2">
              {["free", "paid"].map((p) => (
                <Button key={p} size="sm" variant={form.plan === p ? "default" : "outline"}
                  onClick={() => setForm({ ...form, plan: p })} className="text-xs capitalize" data-testid={`user-plan-${p}`}>
                  {p}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Notes</label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="user-notes-input" placeholder="Internal notes..." />
          </div>
          {isEdit && (
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Access Granted Reason</label>
              <Input value={form.access_granted_reason} onChange={(e) => setForm({ ...form, access_granted_reason: e.target.value })}
                data-testid="user-reason-input" placeholder="e.g. Legacy buyer, support case..." />
            </div>
          )}
          {isEdit && (
            <div className="pt-2 border-t border-slate-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">Download Credits</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Total Limit</label>
                  <Input
                    type="number"
                    min="0"
                    value={form.free_download_limit}
                    onChange={(e) => setForm({ ...form, free_download_limit: Number(e.target.value) })}
                    data-testid="user-download-limit-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Used</label>
                  <Input
                    type="number"
                    min="0"
                    value={form.downloads_used}
                    onChange={(e) => setForm({ ...form, downloads_used: Number(e.target.value) })}
                    data-testid="user-downloads-used-input"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                Remaining: <strong>{Math.max(0, (form.free_download_limit || 0) - (form.downloads_used || 0))}</strong>
                {" · "}Edits are audit-logged.
              </p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving} data-testid="user-modal-cancel">Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="user-modal-save"
            className="bg-[#18181B] hover:bg-[#27272A]">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isEdit ? "Save" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── Announcement Modal ──────────────────────────────────── */
function AnnouncementModal({ ann, onClose, onSave }) {
  const isEdit = !!ann;
  const [form, setForm] = useState({
    title: ann?.title || "",
    description: ann?.description || "",
    cta_text: ann?.cta_text || "",
    cta_link: ann?.cta_link || "",
    priority: ann?.priority ?? 1,
    active: ann?.active ?? true,
    target_audience: ann?.target_audience || "all",
    badge: ann?.badge || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, badge: form.badge || null };
      await onSave(payload);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="announcement-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-[#09090B] mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>
          {isEdit ? "Edit Announcement" : "New Announcement"}
        </h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Title</label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ann-title-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Description</label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ann-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">CTA Text</label>
              <Input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} data-testid="ann-cta-text" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">CTA Link</label>
              <Input value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} data-testid="ann-cta-link" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Priority</label>
              <Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 1 })} data-testid="ann-priority" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Audience</label>
              <select value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
                className="w-full h-9 rounded-md border border-slate-200 px-2 text-xs" data-testid="ann-audience">
                <option value="all">All</option>
                <option value="free">Free</option>
                <option value="paid">Paid</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Badge</label>
              <Input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder="e.g. new" data-testid="ann-badge" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-600">Active</label>
            <button onClick={() => setForm({ ...form, active: !form.active })} data-testid="ann-active-toggle"
              className={`w-9 h-5 rounded-full transition-colors ${form.active ? "bg-green-500" : "bg-slate-300"} relative`}>
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.active ? "left-[18px]" : "left-0.5"}`} />
            </button>
            <span className="text-xs text-slate-500">{form.active ? "Active" : "Inactive"}</span>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving} data-testid="ann-modal-cancel">Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="ann-modal-save"
            className="bg-[#18181B] hover:bg-[#27272A]">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isEdit ? "Save" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── Admin Page ──────────────────────────────────────────── */
export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  // Overview
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Users
  const [users, setUsers] = useState(null);
  const [usersPage, setUsersPage] = useState(1);
  const [usersSearch, setUsersSearch] = useState("");
  const [usersPlanFilter, setUsersPlanFilter] = useState("");
  const [usersSignupFilter, setUsersSignupFilter] = useState(""); // "", "today", "7d", "30d"
  const [usersDownloadsFilter, setUsersDownloadsFilter] = useState(""); // "", "0", "1-3", "4-10", "11+"
  const [usersLoading, setUsersLoading] = useState(false);
  const [userModal, setUserModal] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Images
  const [images, setImages] = useState(null);
  const [imagesPage, setImagesPage] = useState(1);
  const [imagesSearch, setImagesSearch] = useState("");
  const [imagesLoading, setImagesLoading] = useState(false);

  // Announcements
  const [anns, setAnns] = useState([]);
  const [annsLoading, setAnnsLoading] = useState(false);
  const [annModal, setAnnModal] = useState(null); // null | "create" | ann object

  // PIN action queue — {fn: async (pin) => ..., label: string}
  const [pinAction, setPinAction] = useState(null);

  // Guard
  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]" data-testid="admin-forbidden">
        <div className="text-center">
          <p className="text-lg font-semibold text-slate-700 mb-2">Access Denied</p>
          <p className="text-sm text-slate-500 mb-4">Admin role required.</p>
          <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const loadMetrics = async () => {
    setMetricsLoading(true);
    try { const res = await adminAPI.metrics(); setMetrics(res.data); }
    catch { toast.error("Failed to load metrics"); }
    finally { setMetricsLoading(false); }
  };

  const loadUsers = async (p, s, plan, signup, downloads) => {
    setUsersLoading(true);
    try {
      const params = { page: p, limit: 25, search: s, plan: plan };
      // Signup date filter (IST-aware: UTC+5:30)
      const istMidnight = () => {
        const now = new Date();
        // IST midnight = UTC previous day 18:30
        const utc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
        utc.setUTCHours(utc.getUTCHours() - 5);
        utc.setUTCMinutes(utc.getUTCMinutes() - 30);
        // If current UTC time is before 18:30, IST date is still "yesterday"
        if (now < utc) utc.setUTCDate(utc.getUTCDate() - 1);
        return utc;
      };
      if (signup === "today") {
        params.signup_from = istMidnight().toISOString();
      } else if (signup === "7d") {
        const d = istMidnight(); d.setUTCDate(d.getUTCDate() - 7);
        params.signup_from = d.toISOString();
      } else if (signup === "30d") {
        const d = istMidnight(); d.setUTCDate(d.getUTCDate() - 30);
        params.signup_from = d.toISOString();
      }
      // Downloads filter
      if (downloads === "0") {
        params.downloads_min = 0; params.downloads_max = 0;
      } else if (downloads === "1-3") {
        params.downloads_min = 1; params.downloads_max = 3;
      } else if (downloads === "4-10") {
        params.downloads_min = 4; params.downloads_max = 10;
      } else if (downloads === "11+") {
        params.downloads_min = 11;
      }
      const res = await adminAPI.listUsers(params);
      setUsers(res.data);
    }
    catch { toast.error("Failed to load users"); }
    finally { setUsersLoading(false); }
  };

  const loadImages = async (p, s) => {
    setImagesLoading(true);
    try { const res = await adminAPI.listImages({ page: p, limit: 25, search: s }); setImages(res.data); }
    catch { toast.error("Failed to load images"); }
    finally { setImagesLoading(false); }
  };

  const loadAnns = async () => {
    setAnnsLoading(true);
    try { const res = await adminAPI.listAnnouncements(); setAnns(res.data); }
    catch { toast.error("Failed to load announcements"); }
    finally { setAnnsLoading(false); }
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "product-report", label: "Product Report", icon: FileText },
    { id: "analytics", label: "Analytics", icon: TrendingUp },
    { id: "security", label: "Security", icon: Shield },
    { id: "users", label: "Users", icon: Users },
    { id: "otp-dropoffs", label: "OTP Drop-offs", icon: PhoneOff },
    { id: "wa-campaigns", label: "WA Campaigns", icon: MessageCircle },
    { id: "images", label: "Images", icon: Image },
    { id: "announcements", label: "Announcements", icon: Megaphone },
    { id: "coupons", label: "Coupons", icon: Ticket },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-page">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 h-14">
        <div className="max-w-7xl mx-auto h-full flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="h-8 w-8" data-testid="admin-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-bold tracking-tight text-[#18181B]" style={{ fontFamily: "Manrope, sans-serif" }}>
              Admin Dashboard
            </span>
          </div>
          <Badge variant="outline" className="text-xs">admin</Badge>
        </div>
      </header>

      <div className="pt-14 flex">
        {/* Sidebar */}
        <nav className="w-48 shrink-0 border-r border-slate-200 bg-white min-h-[calc(100vh-56px)] py-4 px-2 hidden md:block" data-testid="admin-sidebar">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors mb-0.5 ${
                tab === t.id ? "bg-[#18181B] text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid={`admin-tab-${t.id}`}>
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </nav>

        {/* Mobile tabs */}
        <div className="md:hidden fixed top-14 left-0 right-0 z-40 bg-white border-b border-slate-200 flex overflow-x-auto px-2 py-1" data-testid="admin-mobile-tabs">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors mr-1 ${
                tab === t.id ? "bg-[#18181B] text-white" : "text-slate-500"
              }`}>
              <t.icon className="h-3 w-3" /> {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <main className="flex-1 p-6 md:p-8 max-w-6xl md:mt-0 mt-10">
          {/* ─── Overview ─────────────────────────── */}
          {tab === "overview" && <OverviewTab metrics={metrics} loading={metricsLoading} onLoad={loadMetrics} />}

          {/* ─── Product Report ────────────────────── */}
          {tab === "product-report" && <AdminProductReportTab />}

          {/* ─── Analytics ────────────────────────── */}
          {tab === "analytics" && <AdminAnalyticsTab />}

          {/* ─── Security ─────────────────────────── */}
          {tab === "security" && <AdminSecurityTab />}

          {/* ─── Users ────────────────────────────── */}
          {tab === "users" && (
            <UsersTab data={users} loading={usersLoading} page={usersPage} search={usersSearch}
              planFilter={usersPlanFilter} signupFilter={usersSignupFilter} downloadsFilter={usersDownloadsFilter}
              exporting={exporting}
              onLoad={loadUsers} onPageChange={setUsersPage} onSearchChange={setUsersSearch}
              onPlanFilterChange={setUsersPlanFilter} onSignupFilterChange={setUsersSignupFilter}
              onDownloadsFilterChange={setUsersDownloadsFilter} onSetExporting={setExporting}
              onEdit={(u) => setUserModal(u)} onCreate={() => setUserModal("create")} />
          )}

          {/* ─── OTP Drop-offs ──────────────────────── */}
          {tab === "otp-dropoffs" && <AdminOtpDropoffsTab />}

          {/* ─── WA Campaigns ───────────────────────── */}
          {tab === "wa-campaigns" && <AdminWaCampaignsTab setPinAction={setPinAction} />}

          {/* ─── Images ───────────────────────────── */}
          {tab === "images" && (
            <ImagesTab data={images} loading={imagesLoading} page={imagesPage} search={imagesSearch}
              onLoad={loadImages} onPageChange={setImagesPage} onSearchChange={setImagesSearch}
              onDeleteWithPin={(publicId, dishName) => {
                setPinAction({
                  label: `Delete "${dishName}" record`,
                  fn: async (pin) => {
                    await adminAPI.deleteImage(publicId, pin);
                    toast.success("Image record deleted");
                    loadImages(imagesPage, imagesSearch);
                  },
                });
              }} />
          )}

          {/* ─── Announcements ─────────────────────── */}
          {tab === "announcements" && (
            <AnnouncementsTab anns={anns} loading={annsLoading} onLoad={loadAnns}
              onEdit={(a) => setAnnModal(a)} onCreate={() => setAnnModal("create")} />
          )}

          {/* ─── Coupons ─────────────────────────── */}
          {tab === "coupons" && (
            <AdminCouponsTab
              onPinRequired={(label, action) => {
                setPinAction({ label, action });
              }}
            />
          )}
        </main>
      </div>

      {/* Modals */}
      {userModal && (
        <UserModal
          user={userModal === "create" ? null : userModal}
          onClose={() => setUserModal(null)}
          onSave={async (form) => {
            const isCreate = userModal === "create";
            const userId = isCreate ? null : userModal.user_id;
            // Queue PIN action
            setPinAction({
              label: isCreate ? "Confirm user creation" : "Confirm user update",
              fn: async (pin) => {
                if (isCreate) {
                  await adminAPI.createUser(form, pin);
                  toast.success("User created");
                } else {
                  await adminAPI.updateUser(userId, form, pin);
                  toast.success("User updated");
                }
                setUserModal(null);
                loadUsers(usersPage, usersSearch, usersPlanFilter, usersSignupFilter, usersDownloadsFilter);
              },
            });
          }}
        />
      )}
      {annModal && (
        <AnnouncementModal
          ann={annModal === "create" ? null : annModal}
          onClose={() => setAnnModal(null)}
          onSave={async (form) => {
            if (annModal === "create") {
              await adminAPI.createAnnouncement(form);
              toast.success("Announcement created");
            } else {
              await adminAPI.updateAnnouncement(annModal.id, form);
              toast.success("Announcement updated");
            }
            loadAnns();
          }}
        />
      )}
      {pinAction && (
        <PinModal
          label={pinAction.label}
          onCancel={() => setPinAction(null)}
          onConfirm={async (pin) => {
            // Support both .fn (used by UserModal, ImagesTab) and .action (used by AdminCouponsTab)
            const actionFn = pinAction.fn || pinAction.action;
            await actionFn(pin);
            setPinAction(null);
          }}
        />
      )}
    </div>
  );
}

/* ─── Overview Tab ────────────────────────────────────────── */
function OverviewTab({ metrics, loading, onLoad }) {
  useEffect(() => { onLoad(); }, []);
  if (loading || !metrics) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }
  return (
    <div data-testid="admin-overview">
      <h2 className="text-lg font-bold text-[#09090B] mb-5" style={{ fontFamily: "Manrope, sans-serif" }}>Overview</h2>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricCard label="Total Users" value={metrics.total_users} icon={Users} color="bg-blue-50 text-blue-600" />
        <MetricCard label="Free Users" value={metrics.free_users} icon={UserX} color="bg-slate-100 text-slate-600" />
        <MetricCard label="Paid Users" value={metrics.paid_users} icon={UserCheck} color="bg-green-50 text-green-600" />
        <MetricCard label="Total Images" value={metrics.total_images} icon={Image} color="bg-purple-50 text-purple-600" />
        <MetricCard label="Total Downloads" value={metrics.total_downloads} icon={Download} color="bg-orange-50 text-orange-600" />
        <MetricCard label="Total Revenue" value={`₹${metrics.total_revenue.toLocaleString("en-IN")}`} icon={DollarSign} color="bg-emerald-50 text-emerald-600" />
      </div>
    </div>
  );
}

/* ─── Users Tab ───────────────────────────────────────────── */
function UsersTab({ data, loading, page, search, planFilter, signupFilter, downloadsFilter, exporting, onLoad, onPageChange, onSearchChange, onPlanFilterChange, onSignupFilterChange, onDownloadsFilterChange, onSetExporting, onEdit, onCreate }) {
  const [localSearch, setLocalSearch] = useState(search);
  const debounceRef = { current: null };

  useEffect(() => { onLoad(page, search, planFilter, signupFilter, downloadsFilter); }, [page, search, planFilter, signupFilter, downloadsFilter]);

  const handleSearch = (val) => {
    setLocalSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChange(val);
      onPageChange(1);
    }, 400);
  };

  const handlePlanFilter = (val) => {
    onPlanFilterChange(val);
    onPageChange(1);
  };

  const handleSignupFilter = (val) => {
    onSignupFilterChange(val === signupFilter ? "" : val);
    onPageChange(1);
  };

  const handleDownloadsFilter = (val) => {
    onDownloadsFilterChange(val === downloadsFilter ? "" : val);
    onPageChange(1);
  };

  const handleExport = async (format, exportAll = false) => {
    onSetExporting(true);
    try {
      const plan = exportAll ? "" : planFilter;
      const searchTerm = exportAll ? "" : search;
      const res = await adminAPI.exportUsers(format, plan, searchTerm);
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const ext = format === "xlsx" ? "xlsx" : "csv";
      const planLabel = exportAll ? "all" : (planFilter || "all");
      link.setAttribute("download", `users_${planLabel}_${new Date().toISOString().slice(0, 10)}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${exportAll ? "all" : "filtered"} users to ${format.toUpperCase()}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    } finally {
      onSetExporting(false);
    }
  };

  const planOptions = [
    { value: "", label: "All Plans" },
    { value: "free", label: "Free" },
    { value: "paid", label: "Paid" },
  ];

  const signupOptions = [
    { value: "today", label: "Today" },
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
  ];

  const downloadsOptions = [
    { value: "0", label: "0" },
    { value: "1-3", label: "1–3" },
    { value: "4-10", label: "4–10" },
    { value: "11+", label: "11+" },
  ];

  const hasActiveFilters = planFilter || signupFilter || downloadsFilter || search;

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>Users</h2>
        <Button size="sm" onClick={onCreate} className="bg-[#18181B] hover:bg-[#27272A] text-xs gap-1" data-testid="admin-create-user-btn">
          <Plus className="h-3.5 w-3.5" /> Create User
        </Button>
      </div>
      
      {/* Search + Filter Row */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input value={localSearch} onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search name, phone or email..." className="h-9 pl-9 text-xs" data-testid="admin-users-search" />
        </div>
        
        {/* Plan Filter */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Plan</span>
          <div className="flex gap-1">
            {planOptions.map((opt) => (
              <Button key={opt.value} size="sm" variant={planFilter === opt.value ? "default" : "outline"}
                onClick={() => handlePlanFilter(opt.value)}
                className={`h-7 text-[11px] px-2.5 ${planFilter === opt.value ? "bg-[#18181B]" : ""}`}
                data-testid={`users-filter-${opt.value || "all"}`}>
                {opt.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Signup + Downloads Filters Row */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        {/* Signup date filter */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Signup</span>
          <div className="flex gap-1">
            {signupOptions.map((opt) => (
              <Button key={opt.value} size="sm"
                variant={signupFilter === opt.value ? "default" : "outline"}
                onClick={() => handleSignupFilter(opt.value)}
                className={`h-7 text-[11px] px-2.5 ${signupFilter === opt.value ? "bg-[#18181B]" : ""}`}
                data-testid={`users-signup-${opt.value}`}>
                {opt.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Downloads filter */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Downloads</span>
          <div className="flex gap-1">
            {downloadsOptions.map((opt) => (
              <Button key={opt.value} size="sm"
                variant={downloadsFilter === opt.value ? "default" : "outline"}
                onClick={() => handleDownloadsFilter(opt.value)}
                className={`h-7 text-[11px] px-2.5 ${downloadsFilter === opt.value ? "bg-[#18181B]" : ""}`}
                data-testid={`users-downloads-${opt.value}`}>
                {opt.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Clear all filters */}
        {hasActiveFilters && (
          <button
            onClick={() => { onPlanFilterChange(""); onSignupFilterChange(""); onDownloadsFilterChange(""); onSearchChange(""); setLocalSearch(""); onPageChange(1); }}
            className="text-[11px] text-slate-400 hover:text-slate-600 underline underline-offset-2"
            data-testid="users-clear-filters"
          >
            Clear all
          </button>
        )}
      </div>
      
      {/* Export Buttons */}
      <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-slate-50 rounded-lg border border-slate-100">
        <span className="text-xs font-medium text-slate-600 mr-2">Export:</span>
        <Button size="sm" variant="outline" onClick={() => handleExport("csv", false)} disabled={exporting}
          className="h-7 text-[11px] gap-1" data-testid="export-filtered-csv">
          <FileDown className="h-3 w-3" />
          {planFilter ? `${planFilter.charAt(0).toUpperCase() + planFilter.slice(1)} CSV` : "Filtered CSV"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => handleExport("xlsx", false)} disabled={exporting}
          className="h-7 text-[11px] gap-1" data-testid="export-filtered-xlsx">
          <FileDown className="h-3 w-3" />
          {planFilter ? `${planFilter.charAt(0).toUpperCase() + planFilter.slice(1)} Excel` : "Filtered Excel"}
        </Button>
        <span className="text-slate-300 mx-1">|</span>
        <Button size="sm" variant="outline" onClick={() => handleExport("csv", true)} disabled={exporting}
          className="h-7 text-[11px] gap-1" data-testid="export-all-csv">
          <FileDown className="h-3 w-3" /> All CSV
        </Button>
        <Button size="sm" variant="outline" onClick={() => handleExport("xlsx", true)} disabled={exporting}
          className="h-7 text-[11px] gap-1" data-testid="export-all-xlsx">
          <FileDown className="h-3 w-3" /> All Excel
        </Button>
        {exporting && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400 ml-2" />}
      </div>
      
      {loading || !data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="admin-users-table">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50 text-slate-500">
                    <th className="text-left px-4 py-2.5 font-medium">Name</th>
                    <th className="text-left px-4 py-2.5 font-medium">Phone</th>
                    <th className="text-left px-4 py-2.5 font-medium">Plan</th>
                    <th className="text-left px-4 py-2.5 font-medium">Downloads</th>
                    <th className="text-left px-4 py-2.5 font-medium">Signup</th>
                    <th className="text-left px-4 py-2.5 font-medium">Upgraded</th>
                    <th className="text-left px-4 py-2.5 font-medium">Last Login</th>
                    <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((u) => (
                    <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`user-row-${u.user_id}`}>
                      <td className="px-4 py-2.5 text-[#09090B]" data-testid={`user-name-${u.user_id}`}>{u.first_name || "—"}</td>
                      <td className="px-4 py-2.5 font-medium text-[#09090B]">{u.phone}</td>
                      <td className="px-4 py-2.5">
                        {u.plan === "paid" ? (
                          <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200">
                            {u.plan_id ? u.plan_id.charAt(0).toUpperCase() + u.plan_id.slice(1) : "Paid"}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px]">Free</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">{u.downloads_used} / {u.free_download_limit}</td>
                      <td className="px-4 py-2.5 text-slate-500">{u.created_at_ist || "—"}</td>
                      <td className="px-4 py-2.5 text-slate-500">{u.plan_upgraded_at_ist || "—"}</td>
                      <td className="px-4 py-2.5 text-slate-500">{u.last_login_ist || "—"}</td>
                      <td className="px-4 py-2.5 text-right">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(u)} data-testid={`edit-user-${u.user_id}`}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <SimplePagination page={data.page} totalPages={data.total_pages} onPageChange={onPageChange} />
          <p className="text-[11px] text-slate-400 mt-2">
            {data.total} {planFilter ? `${planFilter} ` : ""}users
            {search && ` matching "${search}"`}
            {signupFilter && ` (signup: ${signupFilter === "today" ? "today" : signupFilter === "7d" ? "last 7 days" : "last 30 days"})`}
            {downloadsFilter && ` (downloads: ${downloadsFilter})`}
          </p>
        </>
      )}
    </div>
  );
}

/* ─── Images Tab ──────────────────────────────────────────── */
function ImagesTab({ data, loading, page, search, onLoad, onPageChange, onSearchChange, onDeleteWithPin }) {
  const [localSearch, setLocalSearch] = useState(search);
  const debounceRef = { current: null };

  useEffect(() => { onLoad(page, search); }, [page, search]);

  const handleSearch = (val) => {
    setLocalSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChange(val);
      onPageChange(1);
    }, 400);
  };

  const handleDelete = async (publicId, dishName) => {
    if (!window.confirm(`Delete "${dishName}" record? (Cloudinary asset is NOT affected)`)) return;
    onDeleteWithPin(publicId, dishName);
  };

  return (
    <div data-testid="admin-images">
      <h2 className="text-lg font-bold text-[#09090B] mb-5" style={{ fontFamily: "Manrope, sans-serif" }}>Images</h2>
      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
        <Input value={localSearch} onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search dish name..." className="h-9 pl-9 text-xs" data-testid="admin-images-search" />
      </div>
      {loading || !data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="admin-images-table">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50 text-slate-500">
                    <th className="text-left px-4 py-2.5 font-medium">Dish Name</th>
                    <th className="text-left px-4 py-2.5 font-medium">Cuisine</th>
                    <th className="text-left px-4 py-2.5 font-medium">Category</th>
                    <th className="text-left px-4 py-2.5 font-medium">Public ID</th>
                    <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.images.map((img) => (
                    <tr key={img.public_id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`image-row-${img.public_id}`}>
                      <td className="px-4 py-2.5 font-medium text-[#09090B]">{img.dish_name}</td>
                      <td className="px-4 py-2.5 text-slate-500">{img.cuisine || "—"}</td>
                      <td className="px-4 py-2.5 text-slate-500">{img.category || "—"}</td>
                      <td className="px-4 py-2.5 text-slate-400 font-mono text-[10px] max-w-[200px] truncate">{img.public_id}</td>
                      <td className="px-4 py-2.5 text-right">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(img.public_id, img.dish_name)} data-testid={`delete-image-${img.public_id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <SimplePagination page={data.page} totalPages={data.total_pages} onPageChange={onPageChange} />
          <p className="text-[11px] text-slate-400 mt-2">{data.total} total images</p>
        </>
      )}
    </div>
  );
}

/* ─── Announcements Tab ───────────────────────────────────── */
function AnnouncementsTab({ anns, loading, onLoad, onEdit, onCreate }) {
  useEffect(() => { onLoad(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await adminAPI.deleteAnnouncement(id);
      toast.success("Announcement deleted");
      onLoad();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleToggle = async (ann) => {
    try {
      await adminAPI.updateAnnouncement(ann.id, { active: !ann.active });
      toast.success(ann.active ? "Deactivated" : "Activated");
      onLoad();
    } catch (err) {
      toast.error("Failed to toggle");
    }
  };

  return (
    <div data-testid="admin-announcements">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>Announcements</h2>
        <Button size="sm" onClick={onCreate} className="bg-[#18181B] hover:bg-[#27272A] text-xs gap-1" data-testid="admin-create-ann-btn">
          <Plus className="h-3.5 w-3.5" /> New Announcement
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : anns.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">No announcements yet.</div>
      ) : (
        <div className="space-y-2">
          {anns.map((a) => (
            <div key={a.id} className={`flex items-center gap-3 bg-white rounded-lg border px-4 py-3 ${a.active ? "border-slate-200" : "border-slate-100 opacity-60"}`}
              data-testid={`ann-row-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-xs font-semibold text-[#09090B] truncate">{a.title}</p>
                  <Badge variant="outline" className="text-[10px]">P{a.priority}</Badge>
                  <Badge variant="secondary" className="text-[10px]">{a.target_audience}</Badge>
                  {a.badge && <Badge className="text-[10px] bg-emerald-100 text-emerald-700">{a.badge}</Badge>}
                  {!a.active && <Badge variant="destructive" className="text-[10px]">Inactive</Badge>}
                </div>
                <p className="text-[11px] text-slate-400 truncate">{a.description}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleToggle(a)}
                  data-testid={`toggle-ann-${a.id}`}>
                  <span className={`block w-2 h-2 rounded-full ${a.active ? "bg-green-500" : "bg-slate-300"}`} />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(a)} data-testid={`edit-ann-${a.id}`}>
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                  onClick={() => handleDelete(a.id)} data-testid={`delete-ann-${a.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
